import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { 
  Star, 
  Calendar, 
  Clock, 
  Play, 
  Plus, 
  Check, 
  ExternalLink
} from 'lucide-react'
import { anilistService } from '@/services/anilist'
import { Anime } from '@/types/anime'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { VideoPlayer } from '@/components/VideoPlayer'
import { AnimeGrid } from '@/components/AnimeGrid'

export default function AnimeDetail() {
  const { id } = useParams<{ id: string }>()
  const [anime, setAnime] = useState<Anime | null>(null)
  const [relatedAnime, setRelatedAnime] = useState<Anime[]>([])
  const [loading, setLoading] = useState(true)
  const [showVideo] = useState(false)
  const [currentEpisode, setCurrentEpisode] = useState(1)
  const [inWatchlist, setInWatchlist] = useState(false)

  useEffect(() => {
    const fetchAnime = async () => {
      if (!id) return

      try {
        setLoading(true)
        const animeData = await anilistService.getAnimeById(Number(id))
        setAnime(animeData)

        // Fetch related anime
        if (animeData.genres.length > 0) {
          const related = await anilistService.getAnimeByGenre(animeData.genres[0], 6)
          setRelatedAnime(related.filter(a => a.id !== animeData.id))
        }
      } catch (error) {
        console.error('Error fetching anime:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchAnime()
  }, [id])

  const handleWatchlistToggle = () => {
    setInWatchlist(!inWatchlist)
    // Here you would integrate with your watchlist API
  }

  const handleNextEpisode = () => {
    if (anime?.episodes && currentEpisode < anime.episodes) {
      setCurrentEpisode(currentEpisode + 1)
    }
  }

  const handlePreviousEpisode = () => {
    if (currentEpisode > 1) {
      setCurrentEpisode(currentEpisode - 1)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!anime) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Anime not found</h1>
          <Link to="/" className="text-blue-400 hover:text-blue-300">
            Go back to home
          </Link>
        </div>
      </div>
    )
  }

  const cleanDescription = anime.description?.replace(/<[^>]*>/g, '')

  return (
    <div className="min-h-screen bg-dark-400">
      {/* Video Player Section */}
      {showVideo && (
        <div className="sticky top-16 z-30 bg-black">
          <div className="container mx-auto px-4 py-4">
            <div className="aspect-video">
              <VideoPlayer
                animeId={anime.id.toString()}
                title={anime.title.english || anime.title.romaji}
                episode={currentEpisode}
                onNext={handleNextEpisode}
                onPrevious={handlePreviousEpisode}
              />
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <div className="relative">
        {anime.bannerImage && (
          <div className="absolute inset-0 h-96">
            <img
              src={anime.bannerImage}
              alt={anime.title.romaji}
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-dark-400 via-dark-400/50 to-transparent" />
          </div>
        )}

        <div className="relative container mx-auto px-4 pt-8 pb-16">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Cover Image */}
            <div className="flex-shrink-0">
              <div className="relative w-64 h-80 mx-auto lg:mx-0">
                <img
                  src={anime.coverImage.large}
                  alt={anime.title.romaji}
                  className="w-full h-full object-cover rounded-lg shadow-2xl"
                />
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 text-center lg:text-left">
              <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
                {anime.title.english || anime.title.romaji}
              </h1>

              {anime.title.native && (
                <p className="text-xl text-gray-300 mb-4">{anime.title.native}</p>
              )}

              {/* Meta Info */}
              <div className="flex flex-wrap justify-center lg:justify-start items-center gap-4 mb-6 text-sm">
                {anime.averageScore && (
                  <div className="flex items-center space-x-1">
                    <Star className="w-4 h-4 text-yellow-400 fill-current" />
                    <span className="text-yellow-400 font-medium">
                      {anime.averageScore}%
                    </span>
                  </div>
                )}
                {anime.seasonYear && (
                  <div className="flex items-center space-x-1 text-gray-300">
                    <Calendar className="w-4 h-4" />
                    <span>{anime.seasonYear}</span>
                  </div>
                )}
                {anime.episodes && (
                  <div className="flex items-center space-x-1 text-gray-300">
                    <Clock className="w-4 h-4" />
                    <span>{anime.episodes} Episodes</span>
                  </div>
                )}
                <div className="px-3 py-1 bg-blue-600 text-white rounded text-xs font-medium uppercase">
                  {anime.format}
                </div>
                <div className={`px-3 py-1 rounded text-xs font-medium uppercase ${
                  anime.status === 'RELEASING' 
                    ? 'bg-green-600 text-white' 
                    : 'bg-gray-600 text-white'
                }`}>
                  {anime.status === 'RELEASING' ? 'ONGOING' : 'COMPLETED'}
                </div>
              </div>

              {/* Genres */}
              <div className="flex flex-wrap justify-center lg:justify-start gap-2 mb-6">
                {anime.genres.map((genre) => (
                  <Link
                    key={genre}
                    to={`/genre/${genre.toLowerCase()}`}
                    className="px-3 py-1 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white rounded-full text-sm transition-colors"
                  >
                    {genre}
                  </Link>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row justify-center lg:justify-start gap-4 mb-8">
                <Link
                  to={`/watch/${anime.id}?episode=1`}
                  className="flex items-center justify-center space-x-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold py-3 px-8 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  <Play className="w-5 h-5" />
                  <span>Watch Now</span>
                </Link>
                
                <button
                  onClick={handleWatchlistToggle}
                  className={`flex items-center justify-center space-x-2 font-semibold py-3 px-8 rounded-lg transition-all duration-200 ${
                    inWatchlist
                      ? 'bg-green-600 hover:bg-green-700 text-white'
                      : 'bg-gray-800 hover:bg-gray-700 text-white border border-gray-600 hover:border-gray-500'
                  }`}
                >
                  {inWatchlist ? <Check className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
                  <span>{inWatchlist ? 'In Watchlist' : 'Add to Watchlist'}</span>
                </button>
              </div>

              {/* Studio */}
              {anime.studios.nodes.length > 0 && (
                <p className="text-gray-400 mb-2">
                  <span className="font-medium">Studio:</span> {anime.studios.nodes[0].name}
                </p>
              )}

              {/* Source */}
              <p className="text-gray-400">
                <span className="font-medium">Source:</span> {anime.source}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content Sections */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            {cleanDescription && (
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">Synopsis</h2>
                <p className="text-gray-300 leading-relaxed">{cleanDescription}</p>
              </section>
            )}

            {/* Episodes */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Episodes</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {Array.from({ length: anime.episodes || 12 }, (_, i) => i + 1).map((episode) => (
                  <Link
                    key={episode}
                    to={`/watch/${anime.id}?episode=${episode}`}
                    className={`block p-4 rounded-lg border transition-all duration-200 ${
                      episode === currentEpisode
                        ? 'bg-blue-600 border-blue-500 text-white'
                        : 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700 hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <Play className="w-4 h-4" />
                      <span className="font-medium">Episode {episode}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>

            {/* Characters */}
            {anime.characters && anime.characters.edges.length > 0 && (
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">Characters</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {anime.characters.edges.slice(0, 8).map((character) => (
                    <div key={character.node.id} className="text-center">
                      <div className="relative w-20 h-20 mx-auto mb-2">
                        <img
                          src={character.node.image.medium}
                          alt={character.node.name.full}
                          className="w-full h-full object-cover rounded-full"
                        />
                      </div>
                      <p className="text-white text-sm font-medium mb-1">
                        {character.node.name.full}
                      </p>
                      <p className="text-gray-400 text-xs">
                        {character.role}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Stats */}
            <div className="bg-gray-800 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Information</h3>
              <div className="space-y-3 text-sm">
                {anime.averageScore && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Score</span>
                    <span className="text-yellow-400 font-medium">{anime.averageScore}%</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-400">Popularity</span>
                  <span className="text-white">{anime.popularity.toLocaleString()}</span>
                </div>
                {anime.episodes && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Episodes</span>
                    <span className="text-white">{anime.episodes}</span>
                  </div>
                )}
                {anime.duration && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Duration</span>
                    <span className="text-white">{anime.duration} min</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-400">Status</span>
                  <span className="text-white">
                    {anime.status === 'RELEASING' ? 'Ongoing' : 'Completed'}
                  </span>
                </div>
                {anime.startDate.year && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Aired</span>
                    <span className="text-white">
                      {anime.startDate.year}
                      {anime.endDate?.year && anime.endDate.year !== anime.startDate.year && 
                        ` - ${anime.endDate.year}`
                      }
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Trailer */}
            {anime.trailer && (
              <div className="bg-gray-800 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Trailer</h3>
                <a
                  href={`https://www.youtube.com/watch?v=${anime.trailer.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center space-x-2 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span>Watch on YouTube</span>
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Related Anime */}
        {relatedAnime.length > 0 && (
          <section className="mt-16">
            <h2 className="text-2xl font-bold text-white mb-6">More Like This</h2>
            <AnimeGrid animeList={relatedAnime} />
          </section>
        )}
      </div>
    </div>
  )
}
